import React, { useMemo, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Grid,
  Box,
  Button,
  Card,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import {
  useGetStudentNoteQuery,
  useCreateStudentNoteMutation,
  useDeleteStudentNoteMutation,
  useToggleStudentNotePinMutation,
} from "../../../Api/attoDeskApi";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { useConfirm, confirmPresets } from "../../../Components/Confirm";
import { useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import dayjs from "dayjs";

interface IStudentNote {
  id: string;
  studentId: string;
  title: string;
  content: string;
  noteType: string;
  isPinned: boolean;
  createdAt: string;
  createdBy?: string;
}

const noteTypeOptions = [
  { value: "", label: "All Types" },
  { value: "GENERAL", label: "General" },
  { value: "ACADEMIC", label: "Academic" },
  { value: "BEHAVIORAL", label: "Behavioral" },
  { value: "MEDICAL", label: "Medical" },
  { value: "FINANCIAL", label: "Financial" },
];

const noteTypeCreateOptions = [
  { value: "GENERAL", label: "General" },
  { value: "ACADEMIC", label: "Academic" },
  { value: "BEHAVIORAL", label: "Behavioral" },
  { value: "MEDICAL", label: "Medical" },
  { value: "FINANCIAL", label: "Financial" },
];

const Index = () => {
  const classes = useStyles();
  const { showErrorMessage, showMessage } = useNotifier();
  const confirm = useConfirm();
  const { studentId } = useParams<{ studentId: string }>();

  const [noteTypeFilter, setNoteTypeFilter] = useState("");
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [newNoteType, setNewNoteType] = useState("GENERAL");

  const { data, isLoading, isError, refetch } = useGetStudentNoteQuery(
    { studentId: studentId || "" },
    { skip: !studentId }
  );

  const [createNote] = useCreateStudentNoteMutation();
  const [deleteNote] = useDeleteStudentNoteMutation();
  const [togglePin] = useToggleStudentNotePinMutation();

  const allNotes: IStudentNote[] = useMemo(() => {
    return Array.isArray(data?.data) ? (data?.data as IStudentNote[]) : [];
  }, [data?.data]);

  const filteredNotes = useMemo(() => {
    let notes = [...allNotes];
    if (noteTypeFilter) {
      notes = notes.filter((n) => n.noteType === noteTypeFilter);
    }
    // Pinned notes first, then by date descending
    notes.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return notes;
  }, [allNotes, noteTypeFilter]);

  const handleCreateNote = async () => {
    if (!newTitle.trim() || !newContent.trim()) {
      showErrorMessage("Please fill in title and content");
      return;
    }
    try {
      const response = await createNote({
        studentId: studentId || "",
        title: newTitle,
        content: newContent,
        noteType: newNoteType,
      }).unwrap();
      if (response.status) {
        showMessage("Note created successfully");
        setOpenCreateDialog(false);
        setNewTitle("");
        setNewContent("");
        setNewNoteType("GENERAL");
        refetch();
      } else {
        showErrorMessage("Failed to create note");
      }
    } catch (error) {
      showErrorMessage("Failed to create note");
    }
  };

  const handleDeleteNote = async (id: string) => {
    const ok = await confirm(confirmPresets.deletePermanent("note"));
    if (!ok) return;
    try {
      const response = await deleteNote(id).unwrap();
      if (response.status) {
        showMessage("Note deleted successfully");
        refetch();
      } else {
        showErrorMessage("Failed to delete note");
      }
    } catch (error) {
      showErrorMessage("Failed to delete note");
    }
  };

  const handleTogglePin = async (id: string) => {
    try {
      const response = await togglePin(id).unwrap();
      if (response.status) {
        showMessage("Note pin toggled");
        refetch();
      } else {
        showErrorMessage("Failed to toggle pin");
      }
    } catch (error) {
      showErrorMessage("Failed to toggle pin");
    }
  };

  const getNoteTypeColor = (type: string) => {
    switch (type) {
      case "ACADEMIC":
        return "primary";
      case "BEHAVIORAL":
        return "warning";
      case "MEDICAL":
        return "error";
      case "FINANCIAL":
        return "info";
      default:
        return "default";
    }
  };

  if (!studentId) {
    return (
      <Card className={classes.card} sx={{ p: 3 }}>
        <Typography variant="body1" color="textSecondary" align="center">
          No student selected. Please access this page from a student profile.
        </Typography>
      </Card>
    );
  }

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <StepUpSpinner size="medium" />
                    </Box>
    );

  return (
    <>
      <Card className={classes.card}>
        <Grid
          container
          justifyContent={"space-between"}
          spacing={2}
          sx={{ padding: 2 }}
          className={classes.titleGrid}
        >
          <Grid item>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Student Notes
            </Typography>
          </Grid>
          <Grid item>
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <TextField
                  select
                  size="small"
                  label="Note Type"
                  value={noteTypeFilter}
                  onChange={(e) => setNoteTypeFilter(e.target.value)}
                  className={classes.searchTextField}
                  sx={{ minWidth: 150 }}
                >
                  {noteTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item>
                <Button
                  className={classes.addNewButton}
                  startIcon={<AddIcon />}
                  onClick={() => setOpenCreateDialog(true)}
                >
                  Add Note
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid sx={{ padding: 2 }}>
          {isError ? (
            <Typography color="error" align="center">
              Error loading notes
            </Typography>
          ) : filteredNotes.length === 0 ? (
            <Typography color="textSecondary" align="center">
              No notes found
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {filteredNotes.map((note) => (
                <Grid item xs={12} key={note.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderLeft: note.isPinned ? "4px solid #1976d2" : "4px solid transparent",
                    }}
                  >
                    <Grid container justifyContent="space-between" alignItems="flex-start">
                      <Grid item xs>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {note.title}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Chip
                              label={note.noteType}
                              size="small"
                              color={getNoteTypeColor(note.noteType) as any}
                              variant="outlined"
                            />
                          </Grid>
                          {note.isPinned && (
                            <Grid item>
                              <Chip label="Pinned" size="small" color="primary" />
                            </Grid>
                          )}
                        </Grid>
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                        >
                          {note.content}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
                          {note.createdBy ? `By ${note.createdBy} - ` : ""}
                          {note.createdAt ? dayjs(note.createdAt).format("YYYY-MM-DD HH:mm") : ""}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Grid container spacing={0}>
                          <Grid item>
                            <IconButton
                              size="small"
                              onClick={() => handleTogglePin(note.id)}
                              color={note.isPinned ? "primary" : "default"}
                            >
                              {note.isPinned ? (
                                <PushPinIcon fontSize="small" />
                              ) : (
                                <PushPinOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Grid>
                          <Grid item>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteNote(note.id)}
                              color="error"
                            >
                              <DeleteOutlineSharpIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Card>

      {/* Create Note Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Note</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Note Type"
                value={newNoteType}
                onChange={(e) => setNewNoteType(e.target.value)}
              >
                {noteTypeCreateOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Content"
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button
            className={classes.addNewButton}
            onClick={handleCreateNote}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Index;
